import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as Yup from 'yup';

import { CardExercise } from '../../components/CardExercise';
import { ModalHeader } from '../../components/ModalHeader';
import { Icon } from '../../components/Icon';
import { InputSearch } from '../../components/InputSearch';
import { Select } from '../../components/Select';
import { TrainingParamsFields } from '../../components/TrainingParamsFields';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { colors } from '../../styles/tokens';
import { ExercisesService } from '../../services/ExercisesService';
import { api } from '../../services/api';
import { formatSplitLabel } from '../../utils/formatSplitLabel';
import { getValidationErrors } from '../../utils/getValidationErrors';
import { IExercise, IMuscleGroup } from '../../domains/Exercise';
import { getExercisePrimaryMedia } from '../../utils/getExercisePrimaryMedia';

import noMidia from '../../assets/no-midia.svg';

import {
  BackButton,
  Body,
  BrowseColumn,
  Container,
  EmptyPanel,
  ExerciseGrid,
  Footer,
  LoadMoreButton,
  PanelColumn,
  ParamsActions,
  ParamsFormContainer,
  SearchFormContainer,
  SectionHeader,
  SectionTitle,
  SelectedExerciseCard,
  SelectedExerciseInfo,
  Submit,
} from './styles';

interface IPendingExerciseData {
  exercise: IExercise;
  interval_min?: number;
  interval_max?: number;
  reps_min?: number;
  reps_max?: number;
  sets_min?: number;
  sets_max?: number;
  additional_information?: string;
}

interface IAddExercisesInTrainingProps {
  user_avatar?: string;
  training_number: number;
  exercises_already_registered_of_day?: IExercise[];
  // Salva de verdade (POST /trainings) -- o modal só reage ao
  // sucesso/erro dessa promise, não fica esperando um "Salvar
  // alterações" na tela de trás.
  onAdd: (data: IPendingExerciseData) => Promise<void>;
  // Vindo da ferramenta de sugestão de treino, `onAdd` só soma ao estado
  // local da prévia -- nada é salvo até "Salvar treino sugerido". Muda o
  // texto do toast (pra não dar a entender que já foi salvo) e mostra um
  // botão de fechar fora do scroll, já que aqui o fluxo normal é fechar
  // o modal em seguida pra voltar pra prévia (diferente do uso na ficha,
  // onde o personal costuma adicionar vários em sequência).
  fromSuggestion?: boolean;
}

// Fallback só pro instante antes do fetch de trainings/gym/settings
// terminar (ver useEffect de mount abaixo) -- mesmos valores que eram
// hardcoded aqui antes de virar configurável por academia (Ajustes da
// academia > Ficha de treino). Intervalo geralmente é definido só como
// um mínimo ("pelo menos 60s de descanso"), sem teto -- por isso não
// tem interval_max.
const FALLBACK_TRAINING_PARAMS = {
  sets_min: 3,
  sets_max: 4,
  reps_min: 8,
  reps_max: 12,
  interval_min: 60,
};

interface ITrainingParamsFormData {
  interval_min: number;
  interval_max: number;
  reps_min: number;
  reps_max: number;
  sets_min: number;
  sets_max: number;
  additional_information: string;
}

const AddExercisesInTraining: React.FC<IAddExercisesInTrainingProps> = ({
  user_avatar,
  training_number,
  exercises_already_registered_of_day,
  onAdd,
  fromSuggestion,
}) => {
  const exercisesService = new ExercisesService();
  const { removeModal } = useModal();

  const paramsFormRef = useRef<FormHandles>(null);

  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [items, setItems] = useState<IExercise[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<IExercise>();
  const [submittingParams, setSubmittingParams] = useState(false);
  // Some da lista assim que o próprio modal confirma um add -- não
  // depende do round-trip de refetch da tela de trás pra sumir da
  // grade (essa é só a fonte que persiste entre reaberturas do modal,
  // via `exercises_already_registered_of_day`).
  const [locallyAddedIds, setLocallyAddedIds] = useState<Set<string>>(
    new Set(),
  );
  const [quickAddingId, setQuickAddingId] = useState<string>();
  const [defaultTrainingParams, setDefaultTrainingParams] = useState(
    FALLBACK_TRAINING_PARAMS,
  );

  const { addToast } = useToast();

  const getMuscleGroups = useCallback(() => {
    exercisesService
      .getMuscleGroups()
      .then(response => {
        setMuscleGroups([{ id: '', name: 'Todos' }, ...response.data]);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
          description:
            'Caso o proplema persista, entre em contato com o administrador do sistema',
        });
      });
  }, [addToast, exercisesService]);

  const fetchPage = useCallback(
    (targetPage: number) =>
      exercisesService
        .getExercisesOfGym({
          page: targetPage,
          limit: 50,
          search: debouncedSearch || undefined,
          muscle_group_id: selectValue || undefined,
        })
        .then(response => {
          setTotalPages(response.data.totalPages);
          setItems(current =>
            targetPage === 1
              ? response.data.data
              : [...current, ...response.data.data],
          );
          setPage(targetPage);
        })
        .catch(err => {
          addToast({
            type: 'error',
            title: 'Erro na busca dos exercicios.',
            description: err?.response?.status,
          });
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, selectValue],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  const handleLoadMore = useCallback(() => {
    setLoadingMore(true);
    fetchPage(page + 1).finally(() => setLoadingMore(false));
  }, [fetchPage, page]);

  const handleChangeSelectForm = useCallback((e: any) => {
    setSelectValue(e.value);
  }, []);

  const handleSubmitParams = useCallback(
    async (data: ITrainingParamsFormData): Promise<void> => {
      if (!selectedExercise) return;

      try {
        setSubmittingParams(true);
        paramsFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          reps_min: Yup.number()
            .nullable(true)
            .transform((currentValue, _) =>
              !Number.isNaN(currentValue) ? Number(currentValue) : undefined,
            ),
          reps_max: Yup.number()
            .nullable(true)
            .transform((currentValue, _) =>
              !Number.isNaN(currentValue) ? Number(currentValue) : undefined,
            ),
          sets_max: Yup.number()
            .nullable(true)
            .transform((currentValue, _) =>
              !Number.isNaN(currentValue) ? Number(currentValue) : undefined,
            ),
          sets_min: Yup.number()
            .nullable(true)
            .transform((currentValue, _) =>
              !Number.isNaN(currentValue) ? Number(currentValue) : undefined,
            ),
          interval_min: Yup.number()
            .nullable(true)
            .transform((currentValue, _) =>
              !Number.isNaN(currentValue) ? Number(currentValue) : undefined,
            ),
          interval_max: Yup.number()
            .nullable(true)
            .transform((currentValue, _) =>
              !Number.isNaN(currentValue) ? Number(currentValue) : undefined,
            ),
          additional_information: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });

        // Salva de verdade na hora -- sem etapa de "Salvar alterações"
        // depois, na tela de trás.
        await onAdd({
          exercise: selectedExercise,
          interval_min: !Number.isNaN(data.interval_min)
            ? data.interval_min
            : undefined,
          interval_max: !Number.isNaN(data.interval_max)
            ? data.interval_max
            : undefined,
          sets_min: !Number.isNaN(data.sets_min) ? data.sets_min : undefined,
          sets_max: !Number.isNaN(data.sets_max) ? data.sets_max : undefined,
          reps_min: !Number.isNaN(data.reps_min) ? data.reps_min : undefined,
          reps_max: !Number.isNaN(data.reps_max) ? data.reps_max : undefined,
          additional_information: data.additional_information?.length
            ? data.additional_information
            : undefined,
        });

        setLocallyAddedIds(current =>
          new Set(current).add(selectedExercise.id),
        );

        addToast({
          type: 'success',
          title: fromSuggestion
            ? 'Adicionado para revisão.'
            : 'Exercício adicionado e salvo no treino.',
        });

        setSelectedExercise(undefined);
        paramsFormRef.current?.reset();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          paramsFormRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro no registro',
          description:
            'Tente novamente. Caso o problema persista, entre em contato com a administração do sistema.',
        });
      } finally {
        setSubmittingParams(false);
      }
    },
    [selectedExercise, addToast, onAdd, fromSuggestion],
  );

  const handleQuickAdd = useCallback(
    async (exercise: IExercise) => {
      setQuickAddingId(exercise.id);

      try {
        await onAdd({ exercise, ...defaultTrainingParams });

        setLocallyAddedIds(current => new Set(current).add(exercise.id));

        addToast({
          type: 'success',
          title: fromSuggestion
            ? 'Adicionado para revisão.'
            : 'Exercício adicionado e salvo no treino.',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro no registro',
          description:
            'Tente novamente. Caso o problema persista, entre em contato com a administração do sistema.',
        });
      } finally {
        setQuickAddingId(undefined);
      }
    },
    [addToast, onAdd, defaultTrainingParams, fromSuggestion],
  );

  // Busca só dispara ~400ms depois da última tecla -- sem isso, cada
  // caractere digitado vira uma chamada nova pro servidor.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Refaz a busca (voltando pra página 1) sempre que o termo ou o grupo
  // muscular mudam -- inclui a busca inicial (ambos começam vazios).
  useEffect(() => {
    setLoading(true);
    fetchPage(1).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectValue]);

  useEffect(() => {
    getMuscleGroups();

    api
      .get('trainings/gym/settings')
      .then(response => {
        setDefaultTrainingParams({
          sets_min: response.data.default_sets_min,
          sets_max: response.data.default_sets_max,
          reps_min: response.data.default_reps_min,
          reps_max: response.data.default_reps_max,
          interval_min: response.data.default_interval_min,
        });
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O servidor já filtra por nome/grupo -- só falta tirar da página o
  // que já está no treino (ou já foi adicionado nesta mesma sessão do
  // modal) antes de exibir.
  const visibleExercises = items.filter(
    exercise =>
      !exercises_already_registered_of_day?.find(
        current => current.id === exercise.id,
      ) && !locallyAddedIds.has(exercise.id),
  );

  const selectedMainMuscleGroup =
    selectedExercise &&
    (selectedExercise.muscle_groups.find(group => group.is_main) ||
      selectedExercise.muscle_groups[0]);

  return (
    <Container>
      <ModalHeader
        avatarUrl={user_avatar}
        title="Adição de exercícios"
        badge={formatSplitLabel(training_number).title}
      />

      <Body>
        <BrowseColumn>
          <SectionHeader>
            <SectionTitle>
              <span>01</span> Exercícios disponíveis
            </SectionTitle>
          </SectionHeader>

          <SearchFormContainer onSubmit={() => undefined}>
            <InputSearch
              name="name"
              placeholder=""
              onChange={handleSearchChange}
            />

            <Select
              name="muscle_groups"
              options={muscleGroups.map(group => ({
                label: group.name as string,
                value: group.id as string,
              }))}
              placeholder="Grupo muscular"
              onChange={handleChangeSelectForm}
              blurInputOnSelect
            />
          </SearchFormContainer>

          <ExerciseGrid>
            {loading ? (
              <EmptyPanel>
                <span>Buscando exercícios...</span>
              </EmptyPanel>
            ) : (
              <>
                {visibleExercises.map(exercise => (
                  <CardExercise
                    key={exercise.id}
                    id={exercise.id}
                    exerciseName={exercise.name}
                    muscleGroups={exercise.muscle_groups}
                    thumbnail_url={
                      getExercisePrimaryMedia(exercise.media)?.thumbnail_url
                    }
                    hasVideo={
                      getExercisePrimaryMedia(exercise.media)?.type === 'video'
                    }
                    disableOptHover
                    compact
                    style={
                      selectedExercise?.id === exercise.id
                        ? {
                            borderColor: colors.primary,
                            backgroundColor: colors.surfaceContainer,
                          }
                        : undefined
                    }
                    changeButtonLabel="Configurar"
                    functionChangeButton={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setSelectedExercise(exercise);
                    }}
                    functionQuickAddButton={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleQuickAdd(exercise);
                    }}
                    quickAddLoading={quickAddingId === exercise.id}
                  />
                ))}

                {visibleExercises.length === 0 && (
                  <EmptyPanel>
                    <span>Nenhum exercício encontrado.</span>
                  </EmptyPanel>
                )}

                {page < totalPages && (
                  <LoadMoreButton
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Carregando...' : 'Carregar mais'}
                  </LoadMoreButton>
                )}
              </>
            )}
          </ExerciseGrid>
        </BrowseColumn>

        <PanelColumn>
          <SectionHeader>
            <SectionTitle>
              <span>02</span> Configurar exercício
            </SectionTitle>
          </SectionHeader>

          {selectedExercise ? (
            <ParamsFormContainer
              ref={paramsFormRef}
              onSubmit={handleSubmitParams}
              initialData={defaultTrainingParams}
            >
              <SelectedExerciseCard>
                <img
                  src={
                    getExercisePrimaryMedia(selectedExercise.media)
                      ?.thumbnail_url || noMidia
                  }
                  alt={selectedExercise.name}
                />
                <SelectedExerciseInfo>
                  <strong>{selectedExercise.name}</strong>
                  <span>
                    {selectedMainMuscleGroup?.name ||
                      selectedMainMuscleGroup?.muscle_group?.name ||
                      ''}
                  </span>
                </SelectedExerciseInfo>
              </SelectedExerciseCard>

              <TrainingParamsFields />

              <ParamsActions>
                <BackButton
                  type="button"
                  onClick={() => setSelectedExercise(undefined)}
                >
                  Cancelar
                </BackButton>
                <Submit
                  name="submit"
                  value="Adicionar exercício"
                  loading={submittingParams}
                />
              </ParamsActions>
            </ParamsFormContainer>
          ) : (
            <EmptyPanel>
              <Icon
                name="fitness_center"
                size={32}
                color={colors.onSurfaceVariant}
              />
              <span>
                Selecione um exercício ao lado para configurar séries,
                repetições e intervalo.
              </span>
            </EmptyPanel>
          )}
        </PanelColumn>
      </Body>

      {fromSuggestion && (
        <Footer>
          <BackButton type="button" onClick={() => removeModal()}>
            Fechar e voltar pra sugestão
          </BackButton>
        </Footer>
      )}
    </Container>
  );
};

export { AddExercisesInTraining };
